import React, { Fragment, useEffect, useState, dispatch } from 'react';
import { connect } from "react-redux";
import { getChildDevice, getDeviceUsagebyId, offboardDeviceById } from '../../action/report';
import Box from '@mui/material/Box';
import Moment from 'react-moment';
import CircularProgress from '@mui/material/CircularProgress';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { getACDeviceStatusKeyMap, normalizeTimestamp } from '../../utils/general';
import customConfig from '../../config';
import AppIconButton from '../Common/Button/IconButton';
import AppTabs from '../Common/Tabs/AppTabs';
import DataTable from '../Common/DataTable/DataTable';

const containerSx = { border: '1px solid #ccc', borderRadius: '8px', padding: '20px', backgroundColor: '#fff', boxShadow: '0px 4px 8px rgba(0,0,0,0.1)', marginTop: '20px' };
const tabsWrapperSx = { overflowX: 'auto', whiteSpace: 'nowrap', marginBottom: '20px' };
const linkContainerSx = { display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '1.5rem' };
const linkBlockSx = { width: '100%', border: '1px solid #ddd', borderRadius: '8px', padding: '1rem', backgroundColor: '#f5f5f5' };

const VRFDeviceDetails = ({ deviceId, getChildDevice, getDeviceUsagebyId, offboardDeviceById, setAlert, onChildCountChange, embedded = false }) => {
  const [childDevicesByAdapter, setChildDevicesByAdapter] = useState({});
  const [selectedAdapterIndex, setSelectedAdapterIndex] = useState(0);
  const [adapterIds, setAdapterIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDeviceId, setExpandedDeviceId] = useState(null);
  const [deviceUsageData, setDeviceUsageData] = useState(null);
  const [selectedChildDevices, setSelectedChildDevices] = useState({});
  const [offboardingLinks, setOffboardingLinks] = useState({});

  const fetchAndProcessChildDevices = async () => {
    if (!deviceId) return;

    setLoading(true);
    getChildDevice(deviceId)
      .then((res) => {
        const grouped = {};
        let validChildren = 0;

        res.forEach((device) => {
          const parts = device?.ieeeAddressParts;
          if (!parts || parts.czAdapterId == null || parts.linkNumber == null) {
            // Some devices may come without parsed IEEE address details.
            // Skip them to avoid runtime crashes while keeping valid devices visible.
            // eslint-disable-next-line no-console
            // console.warn("Skipping child device with missing ieeeAddressParts:", device);
            return;
          }
          const { czAdapterId, linkNumber, iduNumber } = parts;
          validChildren += 1;

          if (!grouped[czAdapterId]) {
            grouped[czAdapterId] = {};
          }
          if (!grouped[czAdapterId][linkNumber]) {
            grouped[czAdapterId][linkNumber] = [];
          }

          grouped[czAdapterId][linkNumber].push({
            iduNumber,
            ...device
          });
        });

        if (typeof onChildCountChange === "function") {
          onChildCountChange(validChildren);
        }

        const ids = Object.keys(grouped).map(Number).sort((a, b) => a - b);
        setChildDevicesByAdapter(grouped);
        setAdapterIds(ids);
        setSelectedAdapterIndex(0);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching child devices:", err);
        setLoading(false);
      });
  }

  useEffect(() => {
    fetchAndProcessChildDevices();
  }, [deviceId]);

  const handleToggleRow = (deviceId) => {
    if (expandedDeviceId === deviceId) {
      setExpandedDeviceId(null);
      setDeviceUsageData(null);
    } else {
      getDeviceUsagebyId(deviceId)
        .then((data) => {
          setDeviceUsageData(data);
          setExpandedDeviceId(deviceId);
        })
        .catch(err => {
          console.error("Error fetching usage:", err);
        });
    }
  };
  
  const selectedAdapterId = adapterIds[selectedAdapterIndex];

  const handleDeviceSelect = (czAdapterId, link, deviceId) => {
  setSelectedChildDevices((prev) => {
    const updated = { ...prev };
    if (!updated[czAdapterId]) updated[czAdapterId] = {};
    if (!updated[czAdapterId][link]) updated[czAdapterId][link] = [];

    const index = updated[czAdapterId][link].indexOf(deviceId);
    if (index !== -1) {
      updated[czAdapterId][link] = updated[czAdapterId][link].filter(id => id !== deviceId);
    } else {
      updated[czAdapterId][link] = [...updated[czAdapterId][link], deviceId];
    }

    return updated;
  });
};


  const handleSelectAll = (czAdapterId, link) => {
  setSelectedChildDevices((prev) => {
    const updated = { ...prev };
    const allDeviceIds = childDevicesByAdapter[czAdapterId][link].map((d) => d.deviceId);
    const currentlySelected = updated[czAdapterId]?.[link] || [];

    if (currentlySelected.length === allDeviceIds.length) {
      if (updated[czAdapterId]) updated[czAdapterId][link] = [];
    } else {
      if (!updated[czAdapterId]) updated[czAdapterId] = {};
      updated[czAdapterId][link] = allDeviceIds;
    }

    return updated;
  });
};


  const handleOffboardDevices = async (czAdapterId, link) => {

  if(!window.confirm(customConfig.confirmOffboard)){
    return;
  };
  
  const devicesToOffboard = selectedChildDevices[czAdapterId]?.[link];
  if (!devicesToOffboard || devicesToOffboard.length === 0) return;

  setOffboardingLinks((prev) => ({
    ...prev,
    [`${czAdapterId}-${link}`]: true
  }));

  try {
    
    let totalSuccess = 0;

    for (const deviceId of devicesToOffboard) {
      try {
        await offboardDeviceById(deviceId);
        totalSuccess++;
      } catch (error) {
        console.log(`Failed to offboard device ${deviceId}`, error);
      }
    }

    if (totalSuccess > 0) {
      setAlert("Device(s) Offboarded Successfully.", "success");
      window.scrollTo(0, 0);
    } else {
      setAlert("Failed to Offboard device(s).", "danger");
    }

    setOffboardingLinks((prev) => ({
      ...prev,
      [`${czAdapterId}-${link}`]: false
    }));

    setSelectedChildDevices((prev) => {
      const updated = { ...prev };
      if (updated[czAdapterId]) updated[czAdapterId][link] = [];
      return updated;
    });

    await fetchAndProcessChildDevices();
  } catch (error) {
    setAlert("Failed to Offboard device(s)", "danger");
    setOffboardingLinks((prev) => ({
      ...prev,
      [`${czAdapterId}-${link}`]: false
    }));
  }
};




  const DeviceUsageDetail = ({deviceUsageData}) => {
    if (!deviceUsageData){
      return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'red',
        fontSize: '1.2rem',
        fontWeight: '500',
        padding: '1rem',
        boxSizing: 'border-box'
      }}>
        {customConfig.noDataMsg}
      </div>
    );
    };
  
    const entries = Object.entries(deviceUsageData);
    const mid = Math.ceil(entries.length / 2);
    const left = entries.slice(0, mid);
    const right = entries.slice(mid);
  
    const renderColumn = (data) => (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {data.map(([key, value]) => (
          <div key={key} className="unique-box mb-15">
            <label className="title d-inline min-180">{getACDeviceStatusKeyMap(key)}</label>
            <div className="device-info d-inline">
              {['ts', 'connectedTime', 'updatedTime', 'nextUpdate'].includes(key)
                ? <Moment format="MMMM Do YYYY, h:mm:ss a" date={normalizeTimestamp(value)} />
                : String(value) || '-'}
            </div>
          </div>
        ))}
      </div>
    );
  
    return (
      <>
        {renderColumn(left)}
        {renderColumn(right)}
      </>
    );
  };
  
  return (
    <Box
      className="vrf-child-devices-card"
      sx={embedded ? { mt: 0, p: 0, border: "none", borderRadius: 0, backgroundColor: "transparent", boxShadow: "none" } : containerSx}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <CircularProgress />
        </div>
      ) : !adapterIds.length ? (
        <div>No adapters found for Device ID: {deviceId}</div>
      ) : (
        <>
          <Box sx={tabsWrapperSx}>
            <AppTabs
              value={selectedAdapterIndex}
              onChange={(e, newIndex) => setSelectedAdapterIndex(newIndex)}
              tabs={adapterIds.map((adapterId) => ({ label: `CZ Adapter ${adapterId}` }))}
              variant="scrollable"
              scrollButtons="auto"
              tabSx={{ minWidth: '120px' }}
            />
          </Box>

          <Box sx={linkContainerSx}>
            {Object.keys(childDevicesByAdapter[selectedAdapterId] || {}).map((link) => (
              <Box key={link} sx={linkBlockSx}>
                <div
                  style={{
                    display: 'flex',              
                    justifyContent: 'space-between', 
                    alignItems: 'center',         
                    backgroundColor: '#d1ecf1',
                    padding: '5px 12px',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    fontSize: '16px',
                    color: '#0c5460'
                  }}
                >
                  <span>Link {link}</span>
                  {childDevicesByAdapter[selectedAdapterId]?.[link]?.length > 0 && (
                    <div style={{ display: 'inline-block', alignItems: 'center' }}>
                      <Checkbox
                        color="primary"
                        checked={
                          selectedChildDevices[selectedAdapterId]?.[link]?.length ===
                          childDevicesByAdapter[selectedAdapterId][link].length
                        }
                        onClick={() => handleSelectAll(selectedAdapterId, link)}

                      />
                      <span>
                        {selectedChildDevices[selectedAdapterId]?.[link]?.length === childDevicesByAdapter[selectedAdapterId][link].length
                          ? 'Unselect All'
                          : 'Select All'}
                      </span>
                    </div>
                  )}
                </div>

                {childDevicesByAdapter[selectedAdapterId]?.[link]?.length > 0 ? (
                  <>
                    <DataTable
                      columns={[
                        { key: 'iduNumber', label: 'IDU Number', minWidth: 120 },
                        { key: 'deviceId', label: 'DeviceId', minWidth: 180 },
                        { key: 'modelNumber', label: 'Model Number', minWidth: 180 },
                        { key: 'productSerialNumber', label: 'Product Serial Number', minWidth: 220 },
                        {
                          key: 'deviceStatus',
                          label: 'Device Status',
                          minWidth: 120,
                          justifyContent: 'center',
                          align: 'center',
                          render: (_, row) => (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <AppIconButton
                                size="small"
                                variant="ghost"
                                onClick={() => handleToggleRow(row.deviceId)}
                                icon={<VisibilityIcon />}
                                tooltip="View device status"
                                aria-label="View device status"
                              />
                            </Box>
                          ),
                        },
                        {
                          key: 'offboard',
                          label: 'Offboard',
                          minWidth: 100,
                          justifyContent: 'center',
                          align: 'center',
                          render: (_, row) => (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Checkbox
                                color="primary"
                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                sx={{ p: 0.5 }}
                                checked={selectedChildDevices[selectedAdapterId]?.[link]?.includes(row.deviceId) || false}
                                onClick={() => handleDeviceSelect(selectedAdapterId, link, row.deviceId)}
                              />
                            </Box>
                          ),
                        },
                      ]}
                      data={[...childDevicesByAdapter[selectedAdapterId][link]].sort((a, b) => a.iduNumber - b.iduNumber)}
                      rowKey="deviceId"
                      minWidth="100%"
                      striped={false}
                      hoverEffect={false}
                      expandedRowKey={expandedDeviceId}
                      renderExpandedRow={(child) => (
                        <Box
                          sx={{
                            p: 2,
                            borderTop: '1px solid #ddd',
                            backgroundColor: '#f9f9f9'
                          }}
                        >
                          <Box
                            sx={{
                              p: 2,
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              backgroundColor: '#eef6fb'
                            }}
                          >
                            <Box sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center', fontSize: '16px', color: '#0c5460' }}>
                              Device Status of DeviceId: {child.deviceId}
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '40px',
                                fontSize: '14px'
                              }}
                            >
                              <DeviceUsageDetail deviceUsageData={deviceUsageData} />
                            </Box>
                          </Box>
                        </Box>
                      )}
                      tableContainerSx={{ boxShadow: 'none', borderRadius: 0 }}
                      msgForDisplay="No devices on this link."
                    />
                  </>
                ) : (
                  <p>No devices on this link.</p>
                )}
                {selectedChildDevices[selectedAdapterId]?.[link]?.length > 0 && (
                  <div style={{ textAlign: 'right', marginTop: '10px' }}>
                    <button
                      className="btn btn-style-3 orange-bg sm font-400 min-140 mqtt_btn"
                      disabled={
                        !selectedChildDevices[selectedAdapterId]?.[link] ||
                        selectedChildDevices[selectedAdapterId][link].length === 0 ||
                        offboardingLinks[`${selectedAdapterId}-${link}`]
                      }
                      onClick={() => handleOffboardDevices(selectedAdapterId, link)}
                    >
                      {offboardingLinks[`${selectedAdapterId}-${link}`] ? (
                        <CircularProgress size="1.5em" style={{ color: 'white', marginTop: '5px' }} />
                      ) : (
                        "Offboard Selected Device"
                      )}
                    </button>
                  </div>
                )}

              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

const mapDispatchToProps = (dispatch) => ({
  getChildDevice: (deviceId) => dispatch(getChildDevice(deviceId)),
  getDeviceUsagebyId: (deviceId) => dispatch(getDeviceUsagebyId(deviceId)),
  offboardDeviceById: (deviceId) => dispatch(offboardDeviceById(deviceId)),
});

export default connect(null, mapDispatchToProps)(VRFDeviceDetails);
